import React from "react";
import DashboardSidebar from "../component/dash/DashboardSidebar";
import themeLarge from "../component/ThemeLarge";
import { showNotification } from "../uti";
import "../css/App.css";
import "react-datepicker/dist/react-datepicker.css";
import { formatPeriodDisplay } from "../uti";
import ProjectEditor from "../component/dash/ProjectEditor";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";

class ProjectAdminView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSidebarOpen: false,
      projects: [],
      selectedProject: null,
      loading: true,
    };
  }

  componentDidMount() {
    this.fetchProjects();
  }

  toggleSidebar = () => {
    this.setState((prev) => ({ isSidebarOpen: !prev.isSidebarOpen }));
  };

  async fetchProjects() {
    try {
      const db = getFirestore();
      const querySnapshot = await getDocs(collection(db, "projects"));
      const projects = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      this.setState({ projects, loading: false });
    } catch (error) {
      console.error("Error fetching projects:", error);
      this.setState({ loading: false });
    }
  }

  updateSelectedProject(field, value) {
    this.setState((prev) => ({
      selectedProject: {
        ...prev.selectedProject,
        [field]:
          field === "keywords" ? value.split(",").map((k) => k.trim()) : value,
      },
    }));
  }

  formatPeriodDate = (str) => {
    if (!str) return null;
    const month = parseInt(str.slice(0, 2)) - 1; // zero-indexed
    const year = parseInt(str.slice(2));
    return new Date(year, month);
  };

  formatToMMYYYY = (date) => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}${yyyy}`;
  };

  validateProject(project) {
    const errors = [];

    if (!project.name.trim()) {
      errors.push("Name cannot be empty.");
    }

    if (
      !project.description ||
      project.description.length < 50 ||
      project.description.length > 300
    ) {
      errors.push("Description must be between 50 and 300 characters.");
    }

    if (
      !Array.isArray(project.keywords) ||
      project.keywords.length < 1 ||
      project.keywords.length > 5
    ) {
      errors.push("Keywords must be 1–5 tags.");
    }

    const urlFields = ["github", "blog", "deploy"];
    urlFields.forEach((field) => {
      try {
        if (project[field]) {
          new URL(project[field]);
        }
      } catch {
        errors.push(`${field} must be a valid URL.`);
      }
    });

    const period = project.period || {};
    const from = period.from;
    const to = period.to;

    if (!from) {
      errors.push("Project start date (from) must be selected.");
    } else {
      const fromMonth = parseInt(from.slice(0, 2));
      const fromYear = parseInt(from.slice(2));

      if (to) {
        // Only validate 'to' if provided
        const toMonth = parseInt(to.slice(0, 2));
        const toYear = parseInt(to.slice(2));

        if (toYear < fromYear || (toYear === fromYear && toMonth < fromMonth)) {
          errors.push("Project period 'to' must be the same or after 'from'.");
        }
      }
    }

    return errors;
  }

  async handleCreateOrUpdateProject(
    project,
    newStatus = "published",
    isCreate = false
  ) {
    if (!project) return;

    if (!window.confirm("Are you sure you want to update this project?")) {
      return;
    }

    const errors = this.validateProject(project);
    if (errors.length > 0) {
      alert("Please fix the following errors:\n" + errors.join("\n"));
      return;
    }

    // Create Firestore
    if (isCreate) {
      try {
        const db = getFirestore();
        const projectRef = collection(db, "projects");

        await addDoc(projectRef, {
          name: project.name,
          description: project.description,
          keywords: project.keywords,
          github: project.github,
          blog: project.blog,
          deploy: project.deploy,
          period: project.period || {},
          status: newStatus,
        });

        showNotification("Project created successfully!", "success");
        this.setState({ showNewProjectForm: false }, () =>
          this.fetchProjects()
        );
      } catch (error) {
        showNotification("Failed to create project!", "error");
        console.error("Error creating project:", error);
      }
    } else {
      // Update Firestore
      try {
        const db = getFirestore();
        const projectRef = collection(db, "projects");

        await updateDoc(doc(projectRef, project.id), {
          name: project.name,
          description: project.description,
          keywords: project.keywords,
          github: project.github,
          blog: project.blog,
          deploy: project.deploy,
          period: project.period || {},
          status: newStatus,
        });

        showNotification("Project updated successfully!", "success");
        this.setState({ selectedProject: null }, () => this.fetchProjects());
      } catch (error) {
        showNotification("Failed to update project!", "error");
        console.error("Error updating project:", error);
      }
    }
  }

  render() {
    const { isSidebarOpen, projects, loading, selectedProject } = this.state;
    const period = selectedProject?.period ?? {};

    return (
      <div
        style={{
          display: "flex",
          height: "calc(100vh - 56px)",
          marginTop: "75px",
        }}
      >
        <DashboardSidebar
          isOpen={isSidebarOpen}
          onToggle={this.toggleSidebar}
        />

        <div
          className="container mt-4"
          style={{ fontFamily: "Kumbh Sans", color: themeLarge.colors.text }}
        >
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ flexWrap: "wrap", marginBottom: "16px" }}
          >
            <p
              style={{
                fontSize: themeLarge.fontSize.h1,
                fontFamily: "Noto Sans",
                color: themeLarge.colors.primary,
                fontWeight: "600",
                marginBottom: 0,
              }}
            >
              Project Admin Panel{" "}
              <span
                style={{
                  fontSize: themeLarge.fontSize.small,
                  paddingLeft: "16px",
                }}
              >
                {projects.length} Projects
              </span>
            </p>

            <button
              className="btn"
              style={{
                background: themeLarge.colors.primary,
                color: themeLarge.colors.secondary,
                padding: "8px 32px",
                borderRadius: "8px",
              }}
              onClick={() =>
                this.setState({
                  selectedProject: null,
                  showNewProjectForm: true,
                })
              }
            >
              Add A Project
            </button>
          </div>

          <div className="row">
            {/* Left side col-md-8 */}
            <div className="col-md-5">
              {loading ? (
                <p>Loading projects...</p>
              ) : projects.length === 0 ? (
                <p>No projects found.</p>
              ) : (
                projects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => {
                      const isSameProject =
                        this.state.selectedProject?.id === project.id;
                      this.setState({
                        selectedProject: isSameProject ? null : { ...project },
                      });
                    }}
                    style={{
                      border: `1px solid ${themeLarge.colors.primary}`,
                      padding: "10px",
                      marginBottom: "10px",
                      borderRadius: "5px",
                      boxShadow: themeLarge.shadows.nothing,
                    }}
                  >
                    <p style={{ fontSize: themeLarge.fontSize.h4 }}>
                      {project.name}
                    </p>
                    <div className="d-flex justify-content-between align-items-center">
                      <p
                        style={{
                          fontSize: themeLarge.fontSize.small,
                          marginBottom: 0,
                        }}
                      >
                        Project Period:{" "}
                        {project.period?.from && project.period?.to
                          ? formatPeriodDisplay(
                              project.period.from,
                              project.period.to
                            )
                          : "---"}
                      </p>

                      {project.status === "hidden" && (
                        <span
                          style={{
                            fontSize: themeLarge.fontSize.small,
                            padding: "2px 8px",
                            backgroundColor: themeLarge.colors.hover, // red badge
                            color: themeLarge.colors.secondary,
                            borderRadius: "999px",
                            marginLeft: "10px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Hidden
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Right side col-md-4 (empty for now) */}
            <div className="col-md-7">
              {selectedProject && (
                <ProjectEditor
                  project={selectedProject}
                  isEditing={true}
                  onClose={() => this.setState({ selectedProject: null })}
                  onSubmit={(updatedProject, status) =>
                    this.handleCreateOrUpdateProject(updatedProject, status)
                  }
                />
              )}

              {this.state.showNewProjectForm && (
                <ProjectEditor
                  isEditing={false}
                  onClose={() => this.setState({ showNewProjectForm: false })}
                  onSubmit={(newProject, status) =>
                    this.handleCreateOrUpdateProject(newProject, status, true)
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ProjectAdminView;
